import apiClient from './api';
import type {
  Catalog,
  CatalogCart,
  CatalogCartItemRequest,
  CatalogView,
  CatalogViewParams,
  CatalogSearchParams,
  CreateCatalogRequest,
  UpdateCatalogRequest,
} from '../types/catalog';
import type { PagedResponse } from '../types/common';

export const catalogService = {
  // Search catalogs with filters and pagination
  search: async (companyId: number, params?: CatalogSearchParams): Promise<PagedResponse<Catalog>> => {
    const response = await apiClient.get('/catalogs', {
      params: { ...params, companyId },
    });
    return response.data;
  },

  // Get catalog by ID
  getById: async (id: number): Promise<Catalog> => {
    const response = await apiClient.get(`/catalogs/${id}`);
    return response.data;
  },

  // Create new catalog
  create: async (catalogData: CreateCatalogRequest): Promise<Catalog> => {
    const response = await apiClient.post('/catalogs', catalogData);
    return response.data;
  },

  // Update catalog
  update: async (id: number, catalogData: UpdateCatalogRequest): Promise<Catalog> => {
    const response = await apiClient.put(`/catalogs/${id}`, catalogData);
    return response.data;
  },

  // Soft delete (archive) catalog
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/catalogs/${id}`);
  },

  // Restore an archived catalog
  restore: async (id: number): Promise<void> => {
    await apiClient.post(`/catalogs/${id}/restore`);
  },

  // Client-side view of a catalog (PUBLIC/RESTRICTED access check on the server)
  getView: async (id: number, params?: CatalogViewParams): Promise<CatalogView> => {
    const response = await apiClient.get(`/catalog-view/${id}`, { params });
    return response.data;
  },

  // Server-persisted cart of the current user for a catalog
  getCart: async (catalogId: number): Promise<CatalogCart> => {
    const response = await apiClient.get(`/catalogs/${catalogId}/cart`);
    return response.data;
  },

  // Replace the whole cart with the desired state
  saveCart: async (catalogId: number, items: CatalogCartItemRequest[]): Promise<CatalogCart> => {
    const response = await apiClient.put(`/catalogs/${catalogId}/cart`, { items });
    return response.data;
  },
};
